<!--
  ExcessesView — Full view for suppliers to manage transaction excesses.

  Related issues:
  - https://github.com/MangoTec/mango-api/issues/79 [CDC-016] Vista de lista de excedentes
  - https://github.com/MangoTec/mango-api/issues/80 [CDC-017] Funcionalidad Marcar como devuelto

  Spec ref: §6.6.5
-->
<template>
  <v-main style="background-color: #edecec">
    <v-container class="py-8 px-6" fluid>
      <v-container>
        <!-- Page header -->
        <v-row>
          <v-col cols="12">
            <div
              class="text-h4 font-weight-bold mt-5 mb-2"
              style="color: #1d1d1f"
            >
              Excedentes
            </div>
            <div class="text-body-1 mb-5" style="color: #6e6e73">
              Gestiona los excedentes de tus transacciones
            </div>
          </v-col>
        </v-row>

        <!-- Summary cards -->
        <v-row v-if="!loading" class="mb-4">
          <v-col cols="12" sm="4">
            <v-card elevation="1" class="pa-4 text-center">
              <div class="text-h5 font-weight-bold" style="color: #f9a825">
                {{ summaryPendingCount }}
              </div>
              <div class="text-caption grey--text">Pendientes</div>
              <div class="text-subtitle-2 font-weight-medium mt-1">
                {{ summaryPendingAmount | currency }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card elevation="1" class="pa-4 text-center">
              <div class="text-h5 font-weight-bold" style="color: #43a047">
                {{ summaryReturnedCount }}
              </div>
              <div class="text-caption grey--text">Devueltos</div>
              <div class="text-subtitle-2 font-weight-medium mt-1">
                {{ summaryReturnedAmount | currency }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" sm="4">
            <v-card elevation="1" class="pa-4 text-center">
              <div class="text-h5 font-weight-bold" style="color: #1d1d1f">
                {{ summaryTotalCount }}
              </div>
              <div class="text-caption grey--text">Total</div>
              <div class="text-subtitle-2 font-weight-medium mt-1">
                {{ summaryTotalAmount | currency }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filters row -->
        <v-row class="mb-2">
          <v-col cols="12" class="d-flex align-center flex-wrap">
            <v-chip-group
              v-model="filter.status"
              mandatory
              active-class="white--text black"
            >
              <v-chip value="all" medium>Todos</v-chip>
              <v-chip value="pending" medium>Pendientes</v-chip>
              <v-chip value="returned" medium>Devueltos</v-chip>
            </v-chip-group>

            <v-spacer></v-spacer>

            <v-btn
              outlined
              small
              @click="showFilters = !showFilters"
              color="#1d1d1f"
              class="mr-2"
            >
              <v-icon left>mdi-filter</v-icon>
              {{ showFilters ? "Ocultar filtro" : "Filtrar" }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Expanded date filters -->
        <v-row v-if="showFilters" class="mb-4">
          <v-col cols="12" sm="4">
            <v-menu
              v-model="menuDateFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filter.date_from"
                  label="Desde"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="filter.date_from = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filter.date_from"
                no-title
                @input="menuDateFrom = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="4">
            <v-menu
              v-model="menuDateTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filter.date_to"
                  label="Hasta"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="filter.date_to = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filter.date_to"
                no-title
                @input="menuDateTo = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="4" class="d-flex align-center">
            <v-btn color="#1d1d1f" dark small @click="applyFilters">
              Aplicar
            </v-btn>
            <v-btn text small @click="resetFilters" class="ml-2">
              Restablecer
            </v-btn>
          </v-col>
        </v-row>

        <!-- Data table -->
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="excesses"
              :loading="loading"
              loading-text="Cargando excedentes..."
              hide-default-footer
              class="elevation-1"
              no-data-text="No se encontraron excedentes"
            >
              <!-- Amount column -->
              <template v-slot:[`item.amount`]="{ item }">
                <span class="font-weight-medium">
                  {{ item.amount | currency }}
                </span>
              </template>

              <!-- Status column -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  text-color="white"
                >
                  {{ getStatusLabel(item.status) }}
                </v-chip>
              </template>

              <!-- Customer column -->
              <template v-slot:[`item.customer.business_name`]="{ item }">
                {{ item.customer ? item.customer.business_name : "—" }}
              </template>

              <!-- Transaction CLABE column -->
              <template
                v-slot:[`item.financial_transaction.clabe`]="{ item }"
              >
                {{
                  item.financial_transaction
                    ? item.financial_transaction.clabe
                    : "—"
                }}
              </template>

              <!-- Transaction amount column -->
              <template
                v-slot:[`item.financial_transaction.amount`]="{ item }"
              >
                <span v-if="item.financial_transaction">
                  {{ item.financial_transaction.amount | currency }}
                </span>
                <span v-else>—</span>
              </template>

              <!-- Return reference column -->
              <template v-slot:[`item.return_reference`]="{ item }">
                {{ item.return_reference || "—" }}
              </template>

              <!-- Created at column -->
              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <!-- Returned at column -->
              <template v-slot:[`item.returned_at`]="{ item }">
                {{ item.returned_at ? formatDate(item.returned_at) : "—" }}
              </template>

              <!-- Actions column -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="item.status === 'pending'"
                  x-small
                  color="#1d1d1f"
                  dark
                  @click="openReturnDialog(item)"
                >
                  <v-icon left x-small>mdi-check-circle-outline</v-icon>
                  Marcar devuelto
                </v-btn>
                <span v-else class="grey--text text--lighten-1">—</span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <!-- Pagination -->
        <v-row v-if="totalPages > 1">
          <v-col cols="12" class="text-center">
            <v-pagination
              v-model="page"
              :length="totalPages"
              :total-visible="6"
              circle
              color="black"
              active-class="black"
            ></v-pagination>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- Mark as returned dialog -->
    <v-dialog v-model="returnDialog.show" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">Marcar como devuelto</v-card-title>

        <v-card-text>
          <p class="text-body-2 mb-4">
            Confirma que devolviste
            <strong v-if="returnDialog.excess">{{ returnDialog.excess.amount | currency }}</strong>
            al cliente
            <strong v-if="returnDialog.excess && returnDialog.excess.customer">{{ returnDialog.excess.customer.business_name }}</strong>.
          </p>

          <v-text-field
            v-model="returnDialog.return_reference"
            label="Referencia bancaria *"
            placeholder="Ej: SPEI123456789"
            outlined
            dense
            :rules="[rules.required]"
            :error-messages="returnDialog.errors.return_reference"
          ></v-text-field>

          <v-textarea
            v-model="returnDialog.notes"
            label="Notas (opcional)"
            placeholder="Información adicional sobre la devolución"
            outlined
            dense
            rows="3"
          ></v-textarea>

          <v-alert
            v-if="returnDialog.errorMsg"
            type="error"
            dense
            class="mt-2"
          >
            {{ returnDialog.errorMsg }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeReturnDialog" :disabled="returnDialog.submitting">
            Cancelar
          </v-btn>
          <v-btn
            color="#1d1d1f"
            dark
            :loading="returnDialog.submitting"
            @click="submitMarkAsReturned"
          >
            Confirmar devolución
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" top>
      {{ snackbar.text }}
    </v-snackbar>
  </v-main>
</template>

<script>
import ExcessService from "@/services/excessService";
import SentryService from "@/services/sentryService";

export default {
  name: "ExcessesView",

  data: () => ({
    excesses: [],
    loading: false,
    page: 1,
    totalPages: 1,
    showFilters: false,
    menuDateFrom: false,
    menuDateTo: false,

    filter: {
      status: "all",
      date_from: null,
      date_to: null,
    },

    // Summary counters
    summaryPendingCount: 0,
    summaryPendingAmount: 0,
    summaryReturnedCount: 0,
    summaryReturnedAmount: 0,
    summaryTotalCount: 0,
    summaryTotalAmount: 0,

    // Mark as returned dialog state
    returnDialog: {
      show: false,
      excess: null,
      return_reference: "",
      notes: "",
      submitting: false,
      errorMsg: "",
      errors: { return_reference: "" },
    },

    // Snackbar for success/error feedback
    snackbar: {
      show: false,
      text: "",
      color: "success",
    },

    // Validation rules
    rules: {
      required: (v) => !!v || "Este campo es obligatorio",
    },

    headers: [
      { text: "ID", value: "id", sortable: false, width: "70px" },
      { text: "Monto excedente", value: "amount", sortable: false },
      { text: "Estado", value: "status", sortable: false },
      {
        text: "Cliente",
        value: "customer.business_name",
        sortable: false,
      },
      {
        text: "CLABE transacción",
        value: "financial_transaction.clabe",
        sortable: false,
      },
      {
        text: "Monto transacción",
        value: "financial_transaction.amount",
        sortable: false,
      },
      {
        text: "Referencia devolución",
        value: "return_reference",
        sortable: false,
      },
      { text: "Fecha", value: "created_at", sortable: false, width: "120px" },
      {
        text: "Devuelto",
        value: "returned_at",
        sortable: false,
        width: "120px",
      },
      {
        text: "Acciones",
        value: "actions",
        sortable: false,
        width: "160px",
        align: "center",
      },
    ],
  }),

  watch: {
    // Re-fetch when status filter changes via chip group
    "filter.status"() {
      if (this.page !== 1) {
        // Changing the page will trigger the page watcher, which fetches data
        this.page = 1;
      } else {
        // Already on page 1, so fetch directly
        this.fetchExcesses();
      }
    },

    // Re-fetch when page changes
    page() {
      this.fetchExcesses();
    },
  },

  async mounted() {
    // Parallelize independent API calls for faster initial load
    await Promise.all([this.fetchSummary(), this.fetchExcesses()]);
  },

  methods: {
    /**
     * Fetch excesses list with current filters and pagination.
     */
    async fetchExcesses() {
      this.loading = true;
      try {
        const supplierId = this.$UserManager.getOwnerId();
        const filters = { page: this.page };

        if (this.filter.status && this.filter.status !== "all") {
          filters.status = this.filter.status;
        }

        if (this.filter.date_from) {
          filters.date_from = this.filter.date_from;
        }

        if (this.filter.date_to) {
          filters.date_to = this.filter.date_to;
        }

        const response = await ExcessService.getExcesses(supplierId, filters);

        if (response && response.ok) {
          const result = await response.json();

          if (result.success) {
            // Handle paginated (data.data) vs flat array (data) response
            if (result.data && result.data.data) {
              this.excesses = result.data.data;
            } else if (Array.isArray(result.data)) {
              this.excesses = result.data;
            } else {
              this.excesses = [];
            }

            // Update pagination from meta
            if (result.meta) {
              this.totalPages = result.meta.last_page || 1;
            } else if (result.data && result.data.last_page) {
              this.totalPages = result.data.last_page;
            }
          }
        }
      } catch (error) {
        SentryService.captureError(error, {
          action: "ExcessesView.fetchExcesses",
          supplierId: this.$UserManager.getOwnerId(),
        });
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch aggregated summary counts and amounts from the dedicated endpoint.
     * Single request instead of iterating through all pages.
     */
    async fetchSummary() {
      try {
        const supplierId = this.$UserManager.getOwnerId();
        const response = await ExcessService.getSummary(supplierId);

        if (response && response.ok) {
          const result = await response.json();

          if (result.success && result.data) {
            this.summaryPendingCount = result.data.pending_count || 0;
            this.summaryPendingAmount = result.data.pending_amount || 0;
            this.summaryReturnedCount = result.data.returned_count || 0;
            this.summaryReturnedAmount = result.data.returned_amount || 0;
            this.summaryTotalCount = result.data.total_count || 0;
            this.summaryTotalAmount = result.data.total_amount || 0;
          }
        }
      } catch (error) {
        SentryService.captureError(error, {
          action: "ExcessesView.fetchSummary",
          supplierId: this.$UserManager.getOwnerId(),
        });
      }
    },

    /**
     * Apply date filters and re-fetch.
     */
    applyFilters() {
      if (this.page !== 1) {
        // Changing the page triggers the page watcher, which fetches data
        this.page = 1;
      } else {
        this.fetchExcesses();
      }
    },

    /**
     * Reset all filters to default values.
     * The filter.status watcher handles re-fetching when status changes,
     * but if status is already "all" we trigger a manual re-fetch.
     */
    resetFilters() {
      const previousStatus = this.filter.status;

      this.filter = {
        status: "all",
        date_from: null,
        date_to: null,
      };

      // If status didn't change, the watcher won't fire — fetch manually
      if (previousStatus === "all") {
        if (this.page !== 1) {
          this.page = 1;
        } else {
          this.fetchExcesses();
        }
      }
    },

    /**
     * Open the mark-as-returned confirmation dialog for a given excess.
     */
    openReturnDialog(excess) {
      this.returnDialog = {
        show: true,
        excess,
        return_reference: "",
        notes: "",
        submitting: false,
        errorMsg: "",
        errors: { return_reference: "" },
      };
    },

    /**
     * Close the dialog and reset its state.
     */
    closeReturnDialog() {
      this.returnDialog.show = false;
      this.returnDialog.excess = null;
      this.returnDialog.return_reference = "";
      this.returnDialog.notes = "";
      this.returnDialog.errorMsg = "";
      this.returnDialog.errors = { return_reference: "" };
    },

    /**
     * Submit the mark-as-returned request to the API.
     */
    async submitMarkAsReturned() {
      // Validate required field
      if (!this.returnDialog.return_reference.trim()) {
        this.returnDialog.errors.return_reference =
          "La referencia bancaria es obligatoria";
        return;
      }

      this.returnDialog.errors.return_reference = "";
      this.returnDialog.errorMsg = "";
      this.returnDialog.submitting = true;

      try {
        const response = await ExcessService.markAsReturned(
          this.returnDialog.excess.id,
          {
            return_reference: this.returnDialog.return_reference.trim(),
            notes: this.returnDialog.notes.trim() || null,
          }
        );

        if (response && response.ok) {
          this.snackbar = {
            show: true,
            text: "Excedente marcado como devuelto exitosamente",
            color: "success",
          };

          this.closeReturnDialog();

          // Refresh both list and summary counters in parallel
          await Promise.all([this.fetchSummary(), this.fetchExcesses()]);
        } else {
          // Try to extract error message from response
          let errorMsg = "Error al marcar como devuelto";

          if (response) {
            try {
              const body = await response.json();
              errorMsg = body.msg || body.message || errorMsg;
            } catch (_) {
              // Response body is not JSON, use default message
            }
          }

          this.returnDialog.errorMsg = errorMsg;
        }
      } catch (error) {
        SentryService.captureError(error, {
          action: "ExcessesView.submitMarkAsReturned",
          excessId: this.returnDialog.excess?.id,
        });

        this.returnDialog.errorMsg = "Error inesperado. Intenta de nuevo.";
      } finally {
        this.returnDialog.submitting = false;
      }
    },

    getStatusColor(status) {
      const colors = {
        pending: "orange",
        returned: "green",
      };

      return colors[status] || "grey";
    },

    getStatusLabel(status) {
      const labels = {
        pending: "Pendiente",
        returned: "Devuelto",
      };

      return labels[status] || status;
    },

    /**
     * Format a date string to a localized short date.
     */
    formatDate(value) {
      if (!value) return "";

      return new Date(value).toLocaleDateString("es-MX", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },

  filters: {
    currency(value) {
      var formatter = new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
      });

      return formatter.format(value);
    },
  },
};
</script>

<style scoped>
.v-data-table >>> th {
  background-color: #f5f5f5 !important;
  color: #1d1d1f !important;
  font-weight: 600 !important;
}
</style>
